import type { ComponentPropsWithoutRef, ReactNode } from "react";
import styles from "./Link.module.css";

/**
 * Link — text hyperlink primitive.
 *
 * Matches the Figma "Link Button" component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1011:413). Always renders a native
 * `<a>` — no polymorphism, no `as` prop. If you need button semantics
 * (onClick without navigation), use `<Button variant="ghost">` instead;
 * Link is for navigation and in-page anchors.
 *
 * Sizes mirror the Figma size enum:
 *   - `xs` — 12px text, line-height 20px, --text-xs-tracking (to match
 *     the xs/Default buttons it often sits next to)
 *   - `s`  — 14px text, line-height 20px (default)
 *
 * Color is `--text-link` which auto-swaps light/dark (teal-600 #0d9488
 * in light, teal-300 #5eead4 in dark). There's no variant enum — the
 * Figma file defines Link Button only in one color token.
 *
 * Accessibility:
 *   - `href` is REQUIRED at the type level. An `<a>` without `href` is
 *     not a hyperlink: it isn't keyboard-focusable, doesn't carry the
 *     implicit `role="link"`, and assistive tech skips it — so rendering
 *     a visual link affordance (cursor + focus ring + underline-on-hover)
 *     on such an element is a false promise. For in-page anchors, use
 *     `href="#anchor"` with an `onClick` handler that calls
 *     `e.preventDefault()`. Sibling primitives enforce a11y-critical
 *     attributes at the type level the same way (IconButton requires
 *     `label`, Input requires `label`).
 *   - Native `<a href>` handles focus + Enter-to-navigate for free.
 *   - Focus-visible renders a 1px outset `--text-primary` ring, matching
 *     Button / IconButton's Figma-derived focus style so sibling primitives
 *     share a consistent keyboard affordance.
 *   - For external links, pass `target="_blank"` and `rel="noopener noreferrer"`
 *     — the primitive does NOT set rel automatically so you get an explicit
 *     signal at the call site.
 */

type Size = "xs" | "s";

export type LinkProps = {
  /** Destination URL. Required — an `<a>` without `href` is not a link. */
  href: string;
  /** Text size. Defaults to `s` (14px). */
  size?: Size;
  children?: ReactNode;
  className?: string;
} & Omit<ComponentPropsWithoutRef<"a">, "className" | "children" | "href">;

const SIZE_CLASS: Record<Size, string> = {
  xs: styles["size-xs"],
  s: styles["size-s"],
};

export function Link({ href, size = "s", className, children, ...rest }: LinkProps) {
  const classes = [styles.link, SIZE_CLASS[size], className].filter(Boolean).join(" ");
  return (
    <a href={href} className={classes} {...rest}>
      {children}
    </a>
  );
}
